import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  type OnDestroy,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import {
  CurrentScopeFacade,
  GlobalStoreService,
  OrganisationService,
  StateBasedNavigationService,
} from '@principle-theorem/ng-principle-shared';
import { Brand, Chat } from '@principle-theorem/principle-core';
import {
  doc,
  doc$,
  filterNil,
  filterUndefined,
} from '@principle-theorem/shared';
import { combineLatest, Subject, type Observable } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { ChatActions } from '../../chat-actions';
import { ChatMessagesBloc } from '../../chat-messages-bloc';
import { getChatSummary$, IChatSummary } from '../../chats-dashboard.store';

@Component({
  selector: 'pr-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChatComponent implements OnDestroy, AfterViewInit {
  private _onDestroy$ = new Subject<void>();
  summary$: Observable<IChatSummary>;
  actions: ChatActions;
  bloc: ChatMessagesBloc;
  @ViewChild('chatContainer') scrollContainer: ElementRef<HTMLElement>;

  constructor(
    private _currentScope: CurrentScopeFacade,
    private _organisation: OrganisationService,
    private _snackBar: MatSnackBar,
    private _route: ActivatedRoute,
    private _stateNav: StateBasedNavigationService,
    private _globalStore: GlobalStoreService
  ) {
    const staffer$ = this._organisation.staffer$.pipe(filterUndefined());
    this.summary$ = this._route.paramMap.pipe(
      map((param) => param.get('uid')),
      filterNil(),
      switchMap((uid) =>
        combineLatest([
          this._currentScope.currentBrand$.pipe(
            filterUndefined(),
            switchMap((brand) => doc$(doc(Brand.chatCol(brand), uid)))
          ),
          staffer$,
        ])
      ),
      switchMap(([chat, staffer]) =>
        getChatSummary$(chat, staffer, this._globalStore)
      ),
      filterUndefined()
    );

    this.actions = new ChatActions(
      this.summary$,
      staffer$,
      this._snackBar,
      this._onDestroy$
    );

    this.bloc = new ChatMessagesBloc(this.actions, staffer$, this._onDestroy$);

    combineLatest([this.summary$, staffer$])
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(([summary, staffer]) => {
        if (Chat.userHasAccess(summary.chat, staffer.ref)) {
          return;
        }

        void this._stateNav.practice(['chats']);
      });
  }

  ngAfterViewInit(): void {
    this.bloc.scrollContainer = this.scrollContainer;
    this.bloc.loadMoreOnScroll();
    this.bloc.clearNewMessagesOnScroll();
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
